import './ModuleList.scss';
import '../../Admin.scss';
import '../../../../styles/_table.scss';
import { Table, TableHead, TableRow, TableCell, TableBody, LoadingPlaceholder, IconButton } from '@trackman/web-shared-components';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import Paragraph from 'components/TextComps/Paragraph';
import { Link } from 'react-router-dom';
import { DefaultRoutes } from 'Routes';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as ArrowDownIcon } from 'images/icons/ArrowDown.svg';
import { ReactComponent as ArrowUpIcon } from 'images/icons/ArrowUp.svg';
import { TableStatusChip, TableStatusChipIntent } from '@trackman/web-shared-components';
import { AdminModuleListEntry, AdminModuleListEntryStatus, AdminModulesSortPayload, AdminModuleStatus } from 'types/admin/modules';
import SectionTitle from 'components/TextComps/SectionTitle';
import { useServices } from 'index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';

export const ModuleList = () => {
  useSetPageTitle({ title: 'Learning Hub Admin', subtitle: 'Administrate and manage the modules here' });

  const [filter, setFilter] = useState<string>('');
  const service = useServices().moduleListService;
  const { data, isPending } = useQuery({ queryKey: ['adminModuleList'], queryFn: service.getModuleList, staleTime: Infinity });
  const filteredModules: AdminModuleListEntry[] | undefined = !filter
    ? data
    : data?.filter((module) => module.title.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className='admin-content-container'>
      {!isPending ? (
        filteredModules && filteredModules.length > 0 ? (
          <>
            <div className='admin-list-header'>
              <HeaderSearchbar searchValue={filter} onChange={(filter) => setFilter(filter)} placeholder='Search Modules' />
              <HeaderCreateButton buttonTitle='Create Module' route={`${DefaultRoutes.ModuleItem}`} />
            </div>

            <div className='tmu-table-wrapper'>
              <Table className='tmu-table admin-table'>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Contents</TableCell>
                    <TableCell>Translations Completed</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredModules.map((row, index) => {
                    return (
                      <ModuleListRow
                        key={index}
                        id={row.id}
                        title={row.title}
                        numberOfCertifications={row.numberOfCertifications}
                        numberOfChapters={row.numberOfChapters}
                        status={row.status}
                        isFirst={index === 0}
                        isLast={data !== undefined && index === data.length - 1}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <div className='empty-state-card'>
            <div>
              <SectionTitle>
                <>No Modules added yet.</>
              </SectionTitle>
              <Paragraph>
                <>No Modules have been added yet.</>
              </Paragraph>
              <Paragraph>
                <>Create one to get started</>
              </Paragraph>
            </div>
            <HeaderCreateButton
              buttonTitle='Create Module'
              route={`${DefaultRoutes.ModuleItem}`}
              customClasses='secondary create-module-button'
            />
          </div>
        )
      ) : (
        <LoadingPlaceholder />
      )}
    </div>
  );
};

type ModuleListRowProps = {
  id: string;
  title: string;
  numberOfChapters: number;
  numberOfCertifications: number;
  status: AdminModuleListEntryStatus;
  isFirst: boolean;
  isLast: boolean;
};
const ModuleListRow = ({ id, title, numberOfChapters, numberOfCertifications, status, isFirst, isLast }: ModuleListRowProps) => {
  const queryClient = useQueryClient();
  const service = useServices().moduleListService;
  const { data, isPending } = useQuery({
    queryKey: ['completedModuleTranslations', id],
    queryFn: () => service.getCompletedTranslations(id),
    staleTime: Infinity,
  });

  const sortChapters = useMutation({
    mutationFn: (payload: AdminModulesSortPayload) => service.sortModules({ moduleId: payload.moduleId, moveUp: payload.moveUp }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['adminModuleList'] }),
  });

  const getModuleStatusLabel = (status: AdminModuleStatus): string => {
    switch (status) {
      case 'ComingSoon':
        return 'Coming Soon';
      case 'Hidden':
        return 'Hidden';
      case 'Visible':
        return 'Visible';
      default:
        return 'Unknown';
    }
  };

  const getModuleStatusIntent = (status: AdminModuleStatus): TableStatusChipIntent => {
    switch (status) {
      case 'ComingSoon':
        return 'info';
      case 'Hidden':
        return 'grey';
      case 'Visible':
        return 'success';
      default:
        return 'grey';
    }
  };
  return (
    <TableRow hover tabIndex={-1} key={id}>
      <TableCell>
        <div>{title}</div>
      </TableCell>
      <TableCell>
        <div className='contents-chapters'>{`${numberOfChapters} Chapter${numberOfChapters > 1 ? 's' : ''}`}</div>
        <div className='contents-certifications'>{`${numberOfCertifications} Certification${numberOfCertifications > 1 ? 's' : ''}`}</div>
      </TableCell>
      <TableCell>
        {!isPending ? (
          <>
            {data && data.length > 0 ? (
              <div className='completed-translations'>{data.join(', ')}</div>
            ) : (
              <span className='no-translations'>No translations completed</span>
            )}
          </>
        ) : (
          <Skeleton />
        )}
      </TableCell>
      <TableCell>
        <TableStatusChip label={getModuleStatusLabel(status)} intent={getModuleStatusIntent(status)}></TableStatusChip>
      </TableCell>
      <TableCell>
        <div className='action-buttons'>
          <Link to={`/Admin/${DefaultRoutes.ModuleItem}/${id}`} className='row-button'>
            <PencilIcon />
          </Link>
          <IconButton
            icon={() => <ArrowUpIcon />}
            style={isFirst ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            onClick={() => (!isFirst ? sortChapters.mutate({ moduleId: id, moveUp: true }) : {})}
          />
          <IconButton
            icon={() => <ArrowDownIcon />}
            style={isLast ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            onClick={() => (!isLast ? sortChapters.mutate({ moduleId: id, moveUp: false }) : {})}
          />
        </div>
      </TableCell>
    </TableRow>
  );
};
